<template>
  <div class="moi-row" :class="{ 'moi-row--selected': isSelected }">
    <span class="moi-name">
      {{ item.name }}
      <span v-if="isSelected" class="moi-picked">{{ selectedLabel }} <span aria-hidden="true">✓</span></span>
    </span>
    <div class="moi-control">
      <materials-order-quantity-entry
        v-if="!quickButtons"
        :item="item"
        :max-quantity="maxQuantity"
        :quantity="quantity"
        @change="$emit('change', $event)"
      />
      <div v-else-if="isCustomOpen" class="moi-custom">
        <materials-order-unit-toggle v-if="hasBoxes" :mode="customMode" :item-name="item.name" @change="switchMode" />
        <materials-order-custom-quantity
          ref="customQuantity"
          :value="customText"
          :material-id="item.material_id"
          :item-name="item.name"
          :max-digits="maxDigits"
          :unit="customUnit"
          @input="typeCustom"
          @clear="closeCustom"
        />
      </div>
      <div v-else class="moi-chips" role="group" :aria-label="`Cantidad de ${item.name}`">
        <button
          v-for="preset in presets"
          :key="preset"
          type="button"
          class="moi-chip"
          :class="{ 'moi-chip--selected': quantity === preset }"
          :aria-pressed="String(quantity === preset)"
          @click="choosePreset(preset)"
        >{{ chipFor(preset).label }}<span v-if="chipFor(preset).hint" class="moi-chip-hint">{{ chipFor(preset).hint }}</span></button>
        <button
          ref="otherChip"
          type="button"
          class="moi-chip"
          :aria-label="`Otra cantidad de ${item.name}`"
          @click="openCustom"
        >Otra</button>
      </div>
    </div>
    <p v-if="isCustomInvalid" class="moi-error">Ingresa una cantidad entre 1 y {{ maxQuantityLabel }}.</p>
    <p v-else-if="isUnderMinimum" class="moi-error">{{ minimumText }}</p>
  </div>
</template>

<script>
import MaterialsOrderCustomQuantity from './MaterialsOrderCustomQuantity.vue';
import MaterialsOrderQuantityEntry from './MaterialsOrderQuantityEntry.vue';
import MaterialsOrderUnitToggle from './MaterialsOrderUnitToggle.vue';
import { formatQuantity } from './materialsOrderSelection';
import {
  UNITS_MODE,
  customQuantityToUnits,
  hasUnitsPerBox,
  isBelowMinimum,
  minimumMessage,
  modeUnitLabel,
  presetChip,
  unitsToModeText,
} from './materialsOrderBoxes';
import { selectedQuantityLabel } from './materialsOrderLayout';

export default {
  name: 'MaterialsOrderItemRow',
  components: { MaterialsOrderCustomQuantity, MaterialsOrderQuantityEntry, MaterialsOrderUnitToggle },
  props: {
    item: { type: Object, required: true },
    presets: { type: Array, required: true },
    maxQuantity: { type: Number, required: true },
    quantity: { type: Number, default: null },
    quickButtons: { type: Boolean, default: true },
  },
  data() {
    const isCustomQuantity = this.quickButtons && this.quantity !== null && !this.presets.includes(this.quantity);
    return {
      isCustomOpen: isCustomQuantity,
      customMode: UNITS_MODE,
      customText: isCustomQuantity ? String(this.quantity) : '',
    };
  },
  computed: {
    isSelected() {
      return this.quantity !== null;
    },
    selectedLabel() {
      return selectedQuantityLabel(this.quantity);
    },
    maxDigits() {
      return String(this.maxQuantity).length;
    },
    maxQuantityLabel() {
      return formatQuantity(this.maxQuantity);
    },
    isCustomInvalid() {
      return this.isCustomOpen && this.customText !== '' && this.customQuantity(this.customText) === null;
    },
    hasBoxes() {
      return hasUnitsPerBox(this.item);
    },
    customUnit() {
      return modeUnitLabel(this.customMode);
    },
    isUnderMinimum() {
      return isBelowMinimum(this.item, this.quantity);
    },
    minimumText() {
      return minimumMessage(this.item);
    },
  },
  methods: {
    chipFor(preset) {
      return presetChip(preset, this.item);
    },
    customQuantity(digits) {
      return customQuantityToUnits(digits, this.customMode, this.item, this.maxQuantity);
    },
    switchMode(mode) {
      this.customMode = mode;
      this.customText = unitsToModeText(this.quantity, mode, this.item);
      this.$emit('change', this.customQuantity(this.customText));
      this.$nextTick(() => this.$refs.customQuantity.focus());
    },
    choosePreset(preset) {
      this.$emit('change', this.quantity === preset ? null : preset);
    },
    openCustom() {
      this.customMode = UNITS_MODE;
      this.customText = this.isSelected ? String(this.quantity) : '';
      this.isCustomOpen = true;
      this.$nextTick(() => this.$refs.customQuantity.focus());
    },
    typeCustom(digits) {
      this.customText = digits;
      this.$emit('change', this.customQuantity(digits));
    },
    closeCustom() {
      this.isCustomOpen = false;
      this.customMode = UNITS_MODE;
      this.customText = '';
      this.$emit('change', null);
      this.$nextTick(() => this.$refs.otherChip.focus());
    },
  },
};
</script>

<style scoped>
.moi-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin: 0 -8px;
  padding: 10px 8px;
  border-bottom: 1px solid #eef1ef;
  border-radius: 8px;
}
.moi-row--selected {
  background: #eef7f1;
  border-bottom-color: transparent;
}
.moi-name {
  flex: 1 1 160px;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 500;
  color: #1f2937;
}
.moi-picked {
  margin-left: 8px;
  font-size: 12.5px;
  font-weight: 700;
  color: #01662E;
  white-space: nowrap;
}
.moi-control {
  display: flex;
  justify-content: flex-end;
  flex: 0 0 auto;
  min-width: 180px;
}
.moi-custom {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  width: 100%;
}
.moi-chip-hint {
  display: block;
  font-size: 10.5px;
  font-weight: 500;
  opacity: 0.75;
}
.moi-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}
.moi-chip {
  min-width: 56px;
  border: 1.5px solid #d3ecde;
  background: #fff;
  color: #01662E;
  font-size: 12.5px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 14px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, color 0.15s ease;
}
.moi-chip:not(.moi-chip--selected):hover { background: #f1f8f4; }
.moi-chip:focus-visible {
  outline: 2px solid #01662E;
  outline-offset: 2px;
}
.moi-chip--selected {
  background: #01662E;
  border-color: #01662E;
  color: #fff;
}
.moi-error {
  flex-basis: 100%;
  margin: 0;
  font-size: 12px;
  color: #b91c1c;
}
@media (max-width: 520px) {
  .moi-name { flex-basis: 100%; }
  .moi-control { justify-content: flex-start; }
}
@media (prefers-reduced-motion: reduce) {
  .moi-chip { transition: none; }
}
</style>
